"use client";

import { useCallback, useEffect, useState } from "react";
import CraftQuiz, { CraftNumbers, CraftView } from "./CraftQuiz";
import { CRAFT_CSS } from "./craftStyle";
import { JUDGMENT_LABEL } from "./characterViews";

const WORLD = "Baunheim";

interface Sample {
  id: string;
  name: string;
  class: string;
  pass: number;
  /** 作製に使う判定実数値の綴り。crafting・compounding・cooking */
  judgment?: string;
}
interface Limits {
  stat: number;
  factor: number;
  factor_step: number;
  bonus: number;
}

async function call(op: string, args: Record<string, unknown>) {
  const response = await fetch("/api/admin/craft-demo", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ world: WORLD, op, args }),
  });
  const body = await response.json();
  if (!response.ok || typeof body.error === "string") throw new Error(body.error ?? "デモを読めません");
  return body;
}

/**
 * 作製デモプレイ。見本の作製を、実数値・係数・加算を決めて試す。
 * 進行の画面と同じクイズの部品を使い、数（初期値・合格点・線・答えの値）も出す
 */
export default function CraftDemo({ onBack }: { onBack: () => void }) {
  const [samples, setSamples] = useState<Sample[]>([]);
  const [limits, setLimits] = useState<Limits | null>(null);
  const [sample, setSample] = useState("");
  const [stat, setStat] = useState(30);
  const [factor, setFactor] = useState(1);
  const [bonus, setBonus] = useState(0);
  const [seed, setSeed] = useState(1);
  const [answers, setAnswers] = useState<number[]>([]);
  const [playing, setPlaying] = useState(false);
  const [view, setView] = useState<CraftView | null>(null);
  const [numbers, setNumbers] = useState<CraftNumbers | null>(null);
  const [feel, setFeel] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    call("samples", {})
      .then((body) => {
        setSamples(body.samples as Sample[]);
        setLimits(body.limits as Limits);
        setSample((body.samples as Sample[])[0]?.id ?? "");
      })
      .catch((error) => setMessage(error instanceof Error ? error.message : "デモを読めません"));
  }, []);

  const play = useCallback(
    async (nextAnswers: number[], nextSeed: number) => {
      setBusy(true);
      setMessage("");
      try {
        const body = await call("play", { sample, stat, factor, bonus, seed: nextSeed, answers: nextAnswers });
        setView(body.craft as CraftView);
        setNumbers(body.numbers as CraftNumbers);
        setFeel(body.feel ?? "");
        setAnswers(nextAnswers);
        setPlaying(true);
      } catch (error) {
        setMessage(error instanceof Error ? error.message : "デモを進められません");
      } finally {
        setBusy(false);
      }
    },
    [sample, stat, factor, bonus]
  );

  const start = () => {
    const nextSeed = Math.floor(Math.random() * 1000000) + 1;
    setSeed(nextSeed);
    play([], nextSeed);
  };

  const back = () => {
    setPlaying(false);
    setView(null);
    setFeel("");
  };

  return (
    <div className="cf-root">
      <style dangerouslySetInnerHTML={{ __html: CRAFT_CSS }} />
      <div className="cf-app">
        <header className="cf-head">
          <h2>作製デモプレイ</h2>
          <button type="button" className="cf-head-btn" onClick={playing ? back : onBack}>
            {playing ? "支度へ戻る" : "戻る"}
          </button>
        </header>
        <div className="cf-body">
          {playing && view ? (
            <CraftQuiz view={view} feel={feel} busy={busy} numbers={numbers} onAnswer={(index) => play([...answers, index], seed)} />
          ) : (
            <>
              <div className="cf-card">
                <div className="cf-samples">
                  {samples.map((one) => (
                    <button key={one.id} type="button" className={sample === one.id ? "cf-on" : ""} onClick={() => setSample(one.id)}>
                      {one.name}
                      <small>
                        {one.class}・合格点 {one.pass}
                      </small>
                    </button>
                  ))}
                </div>
              </div>
              {limits ? (
                <div className="cf-card">
                  <label className="cf-field">
                    {JUDGMENT_LABEL[samples.find((one) => one.id === sample)?.judgment ?? "crafting"] ?? "製作"}の実数値
                    <input type="range" min={0} max={limits.stat} step={1} value={stat} onChange={(event) => setStat(Number(event.target.value))} />
                    <output>{stat}</output>
                  </label>
                  <label className="cf-field">
                    係数
                    <input type="range" min={0} max={limits.factor} step={limits.factor_step} value={factor} onChange={(event) => setFactor(Number(event.target.value))} />
                    <output>{factor}</output>
                  </label>
                  <label className="cf-field">
                    加算
                    <input type="range" min={0} max={limits.bonus} step={1} value={bonus} onChange={(event) => setBonus(Number(event.target.value))} />
                    <output>{bonus}</output>
                  </label>
                  <p className="cf-num">
                    初期値 <b>{Math.round((stat * factor + bonus) * 100) / 100}</b>
                  </p>
                </div>
              ) : null}
            </>
          )}
          {message ? <p className="cf-message">{message}</p> : null}
        </div>
        <div className="cf-foot">
          {playing ? (
            <button type="button" className="cf-main-btn" onClick={start} disabled={busy}>
              同じ支度でもう一度
            </button>
          ) : (
            <button type="button" className="cf-main-btn" onClick={start} disabled={busy || sample === ""}>
              作製を始める
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
